import type { ColumnDef } from "@tanstack/react-table";

import type { Page } from "../../api";
import { Chip } from "../../chips";
import { ItemLink } from "../../links";
import { TabPage } from "../../PageFrame";
import { FUNNEL_LABELS } from "../../SharedFilterBar";
import type { PageProps } from "../index";
import { Footnote, MUTED_ROW, PAGE_SIZE, PagedTable, PageHead, usePage } from "../kit";

export interface MarginItemRow {
  funnel: string;
  item_id: string;
  item_name: string;
  type: string | null;
  status: string | null;
  margin_item: boolean;
  margin_n: number;
}

export const MARGIN_ITEMS_NOTE =
  "Read-only. To change it, ask Claude to tick or untick “Margin item” on the item's Notion page (#1 SRC DESIGN: ITEM); the change shows here within about 5 minutes.";

export const MARGIN_ITEM_COLUMNS: ColumnDef<MarginItemRow, unknown>[] = [
  {
    id: "item",
    header: "Item",
    cell: ({ row }) => (
      <ItemLink itemId={row.original.item_id} from="margin_items">
        {row.original.item_name}
      </ItemLink>
    ),
  },
  { id: "funnel", header: "Funnel", cell: ({ row }) => FUNNEL_LABELS[row.original.funnel] ?? row.original.funnel },
  { id: "type", header: "Type", cell: ({ row }) => row.original.type ?? "—" },
  { id: "status", header: "Status", cell: ({ row }) => row.original.status ?? "—" },
  {
    id: "margin_item",
    header: "Margin item",
    cell: ({ row }) => row.original.margin_item ? <Chip tone="info">Margin item</Chip> : "—",
  },
];

export function MarginItemsBody({ data, page, onPage }: { data: Page<MarginItemRow>; page: number; onPage: (next: number) => void }) {
  return (
    <>
      <PagedTable<MarginItemRow>
        columns={MARGIN_ITEM_COLUMNS}
        data={data}
        page={page}
        onPage={onPage}
        caption="Items and their margin flag"
        rowClassName={(row) => row.margin_item ? undefined : MUTED_ROW}
      />
      <Footnote>{MARGIN_ITEMS_NOTE}</Footnote>
    </>
  );
}

export function MarginItemsPage({ tab, areaName }: PageProps) {
  const [page, setPage] = usePage();
  return (
    <TabPage<Page<MarginItemRow>>
      tab={tab}
      areaName={areaName}
      extra={{ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }}
      isEmpty={(data) => data.total === 0}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={data ? "Items ranked on Margin at Stake and listed on Stalled" : undefined}
          kpi={data ? { label: "Margin items", value: data.items[0]?.margin_n ?? 0 } : null}
        />
      )}
    >
      {(data) => <MarginItemsBody data={data} page={page} onPage={setPage} />}
    </TabPage>
  );
}
